/*
 * @author Andrey Patyuchenko
 * @email  apatyuchenko@atlassian.com
 * @email  andrey.patyuchenko@gmail.com
 * @date   7/16/20 1:17 PM
 */

import React from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faAtlassian, faConfluence, faJira, faSlack} from "@fortawesome/free-brands-svg-icons";
import {format} from "date-fns"
import {
	Alert,
	AlertTitle,
	Box,
	Divider, Grid,
	Link,
	Paper, Stack,
	Typography
} from "@mui/material";
import Const from "./const.json";
import {useMutation} from "urql";
import {useDispatch, useSelector} from "react-redux";
import {useLocation, useNavigate} from "react-router-dom";
import {login} from "./state/reducers/user";
import {LoadingButton} from "@mui/lab";
import {SamlLoginMutation, SamlLoginResponse} from "./graphql/mutations/saml-login";
import {getDataUrl} from "./context/environment";
import HeroImg from "./media/images/hero.svg";
import {State} from "./state/store";

export default function Login() {

	const user = useSelector((s:State) => s.user);
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();

	const [isLoading, setIsLoading] = React.useState(true);
	const [inCallback, setInCallback] = React.useState(false);
	const [errorMessage, setErrorMessage] = React.useState<React.ReactNode|string|null>(null);

	const url = new URL(window.location.href);

	const [result, samlLogin] = useMutation<SamlLoginResponse>(SamlLoginMutation);

	React.useEffect(() => {
		if (isLoading && !inCallback) {
			if (window.location.href.includes("from") || (process.env.NODE_ENV === "development" && process.env.NEBULAE !== "true")) {
				setErrorMessage(null);
				setInCallback(true);
				samlLogin().then(response => {
					if (response.error) {
						if (response.error.networkError) {
							setErrorMessage("Network error: Make sure you are connected to Atlassian VPN.");
						} else {
							setErrorMessage(response.error.message);
						}
						setIsLoading(false);
					} else {
						if (response.data?.samlLogin.ok) {
							const { ok: omit, ...user } = response.data.samlLogin
							console.log("Login Successful, welcome", response.data.samlLogin.firstName);
							console.log("Your locale is", navigator.language, "(" + user.location + ")");
							dispatch(login(user));
							navigate(url.searchParams.get("from") || "/");
						} else {
							setErrorMessage(<div>
								<div>You are not authorized to access this service.</div>
								<div>Please contact us at <Link href={Const.URL_SLACK_CHANNEL} target="_blank">
									{Const.SLACK_CHANNEL_NAME}</Link>.
								</div>
							</div>);
							setIsLoading(false);
						}
					}
				}).catch(err => {
					setIsLoading(false);
					setErrorMessage(err.message);
				});
			} else if (!user && !result.data) {
				setErrorMessage(null);
				const from = (location.state as { from: string })?.from || "/";
				const redirectUrl = encodeURI(window.location.href + "?from=" + from);
				const samlLoginUrl = getDataUrl() + `/saml/login?redirect=${redirectUrl}`;
				window.location.replace(samlLoginUrl);
			}
		}
	}, [dispatch, navigate, result.data, samlLogin, url.searchParams, user, isLoading, inCallback, location.state]);

	const logBackIn = () => {
		setIsLoading(true);
		setInCallback(false);
		navigate("/login");
	};

	return (
		<Grid container alignContent="center" alignItems="center" direction="column">
			<Box m={6} />
			<Paper elevation={3} sx={{ width: "550px", maxWidth: "550px" }}>
				<Box p={3}>
					<Grid container alignContent="center" alignItems="center" direction="column">
						<div>
							<img src={HeroImg} alt={"Vega Calendar"} style={{width: "300px"}} />
						</div>
						<div>
							<Typography variant="h4">
								Vega Calendar
							</Typography>
						</div>
						<Box m={1} color="text.secondary" fontSize="fontSize">
							Version {process.env.REACT_APP_VERSION} ({format(Number(process.env.REACT_APP_BUILD_DATE), 'MMMM do, yyyy')})
						</Box>
						<Box m={1}>
							<Typography variant="body1">
								Human-friendly calendar for Atlassian teams
							</Typography>
						</Box>
						<Box m={2}>
							<LoadingButton
								variant="contained"
								size="large"
								color={"primary"}
								loading={isLoading}
								onClick={logBackIn}
								loadingPosition={"start"}
								startIcon={<FontAwesomeIcon icon={faAtlassian}/>}
							>
								Login with Atlassian ID
							</LoadingButton>
						</Box>
						{errorMessage &&
						<Box m={2}>
							<Alert severity="error" variant={"outlined"}>
								<AlertTitle>We couldn't log you in</AlertTitle>
								<div>{errorMessage}</div>
							</Alert>
						</Box>}
						<Stack m={2} spacing={2} direction={"row"} divider={<Divider orientation="vertical" flexItem />}>
							<Box>
								<FontAwesomeIcon style={{color: "#0065ff"}} icon={faConfluence} size="sm"/> <Link underline={"hover"} target="_blank" href={Const.URL_DOCS}>Docs</Link>
							</Box>
							<Box>
								<FontAwesomeIcon style={{color: "#998DD9"}} icon={faSlack} size="sm"/> <Link underline={"hover"} target="_blank" href={Const.URL_SLACK_CHANNEL}>{Const.SLACK_CHANNEL_NAME}</Link>
							</Box>
							<Box>
								<FontAwesomeIcon style={{color: "#0065ff"}} icon={faJira} size="sm"/> <Link underline={"hover"} target="_blank" href={Const.URL_CONTACT}>Contact us</Link>
							</Box>
							<Box>
								<FontAwesomeIcon style={{color: "#0065ff"}} icon={faConfluence} size="sm"/> <Link underline={"hover"} target="_blank" href={Const.URL_CST_CONFLUENCE}>go/cst</Link>
							</Box>
						</Stack>
					</Grid>
				</Box>
			</Paper>
		</Grid>
	);

}
